import React, { Fragment, useCallback, useRef, forwardRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import ListenToKeyboard from 'components/ListenToKeyboard'
import MatrixBasicMessageComposer from 'matrix-react-sdk/lib/components/views/rooms/BasicMessageComposer'
import { getCaretOffsetAndText } from 'matrix-react-sdk/lib/editor/dom'
import DocumentOffset from 'matrix-react-sdk/lib/editor/offset'
import { setCaretPosition } from 'matrix-react-sdk/lib/editor/caret'
import { selectAccountChatRoomId } from 'selectors/app/selectAccountPreferences'
import { selectIsChatAvailableForCurrentWidget } from 'ducks/widgets/selectors'
import selectCurrentConversationId from 'ducks/searches/selectors/selectCurrentConversationId'
import { doChangeEditorFocus } from 'ducks/editor/operations'
import { doAgentStartTicketTypingNotification } from 'ducks/collisions/actions'
import { selectCurrentUserIsAdminOrOwnerOrAgent } from 'ducks/currentUser/selectors'

const BasicMessageComposer = forwardRef((props, ref) => {
  const { onBlur, onFocus } = props
  const basicMessageComposerRef = useRef(null)
  const dispatch = useDispatch()

  // Redux state selectors
  const isAdminOrOwnerOrAgent = useSelector(
    selectCurrentUserIsAdminOrOwnerOrAgent
  )
  const accountChatRoomId = useSelector(selectAccountChatRoomId)
  const conversationId = useSelector(selectCurrentConversationId)
  const enabled = useSelector(selectIsChatAvailableForCurrentWidget)

  // Set editor caret to the end
  const setEditorCaretToEnd = useCallback(() => {
    const composer = basicMessageComposerRef.current
    const editor = composer.getEditableRootNode()
    const editorModel = composer.props.model
    const position = editorModel.getPositionAtEnd()
    // eslint-disable-next-line no-underscore-dangle
    composer._setLastCaretFromPosition(position)
    setCaretPosition(editor, editorModel, position)
  }, [])

  // Handle onBlur event
  const handleOnBlur = useCallback(
    () => {
      dispatch(doChangeEditorFocus(false))
      if (onBlur) onBlur(this)
    },
    [dispatch, onBlur]
  )

  // Handle onFocus event
  const handleOnFocus = useCallback(
    () => {
      dispatch(doChangeEditorFocus(true))

      const sel = document.getSelection()
      const composer = basicMessageComposerRef.current
      const editor = composer.getEditableRootNode()
      const editorModel = composer.props.model
      const { text } = getCaretOffsetAndText(editor, sel)

      if (!isAdminOrOwnerOrAgent && !text.startsWith('** ')) {
        editorModel.update(
          `** ${text}`,
          'insertText',
          new DocumentOffset(3, false)
        )
        // eslint-disable-next-line no-underscore-dangle
        composer._modifiedFlag = true

        setEditorCaretToEnd()
      }
      if (onFocus) onFocus(this)
    },
    [dispatch, onFocus, setEditorCaretToEnd, isAdminOrOwnerOrAgent]
  )

  // Handle 'R' key press
  const handleRPress = useCallback(
    () => {
      if (!isAdminOrOwnerOrAgent) return
      const sel = document.getSelection()
      const composer = basicMessageComposerRef.current
      const editor = composer.getEditableRootNode()
      const { text } = getCaretOffsetAndText(editor, sel)
      if (text.startsWith('** ')) {
        composer.props.model.update(
          text.substr(3),
          'insertText',
          new DocumentOffset(0, false)
        )
        // eslint-disable-next-line no-underscore-dangle
        composer._modifiedFlag = true
      }
      setEditorCaretToEnd()
      composer.focus()
    },
    [setEditorCaretToEnd, isAdminOrOwnerOrAgent]
  )

  // Handle 'N' key press
  const handleNPress = useCallback(
    () => {
      const sel = document.getSelection()
      const composer = basicMessageComposerRef.current
      const editor = composer.getEditableRootNode()
      const editorModel = composer.props.model
      const { text } = getCaretOffsetAndText(editor, sel)
      if (!text.startsWith('** ')) {
        editorModel.update(
          `** ${text}`,
          'insertText',
          new DocumentOffset(3, false)
        )
        // eslint-disable-next-line no-underscore-dangle
        composer._modifiedFlag = true
      }
      setEditorCaretToEnd()
      composer.focus()
    },
    [setEditorCaretToEnd]
  )

  // Handle onKeyDown event
  const handleOnKeyDown = useCallback(
    () => {
      dispatch(doAgentStartTicketTypingNotification(conversationId))
      const sel = document.getSelection()
      const composer = basicMessageComposerRef.current
      const editor = composer.getEditableRootNode()
      const editorModel = composer.props.model
      const { text } = getCaretOffsetAndText(editor, sel)

      if (!isAdminOrOwnerOrAgent && !text.startsWith('** ')) {
        editorModel.update(
          `** ${text}`,
          'insertText',
          new DocumentOffset(3, false)
        )
        // eslint-disable-next-line no-underscore-dangle
        composer._modifiedFlag = true

        setEditorCaretToEnd()
      }
    },
    [dispatch, conversationId, isAdminOrOwnerOrAgent, setEditorCaretToEnd]
  )

  // Save ref for the basic message composer
  const saveRef = useCallback(
    composerRef => {
      basicMessageComposerRef.current = composerRef
      if (ref) ref(composerRef)
    },
    [ref]
  )

  if (!enabled) return null

  return (
    <Fragment>
      <ListenToKeyboard
        disableForInput
        preventDefault
        onR={handleRPress}
        onN={handleNPress}
      />
      <MatrixBasicMessageComposer
        {...props}
        accountChatRoomId={accountChatRoomId}
        showFormatBar={false}
        focusOnMount={false}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onKeyDown={handleOnKeyDown}
        ref={saveRef}
      />
    </Fragment>
  )
})

export default BasicMessageComposer
